import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, CalendarClock, Building2, MapPin, FileText, Repeat,
  Users, CalendarOff, ListTodo, BarChart3, FolderOpen, Wand2, Bell, ShieldCheck,
  History, Settings as SettingsIcon, Search, LogOut, Menu, X, ChevronDown, Briefcase,
  Wallet, Mail, FileUp, ClipboardList,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { can, isConsultant } from "@/lib/permissions";
import { api } from "@/lib/api";
import { SeqLogo, SeqMark } from "@/components/SeqLogo";
import { initials } from "@/lib/format";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, module: "scheduling" },
  { to: "/unscheduled", label: "Unscheduled Work", icon: CalendarClock, perm: "schedule_jobs", module: "scheduling" },
  { to: "/clients", label: "Clients", icon: Building2, perm: "view_clients", module: "clients" },
  { to: "/sites", label: "Sites", icon: MapPin, perm: "view_sites", module: "clients" },
  { to: "/contracts", label: "Contracts", icon: FileText, perm: "view_clients", module: "contracts" },
  { to: "/job-templates", label: "Job Templates", icon: Repeat, perm: "create_job_templates", module: "job_templates" },
  { to: "/forms", label: "Custom Forms", icon: ClipboardList, perm: "manage_system_settings" },
  { to: "/consultants", label: "Consultants", icon: Users },
  { to: "/leave", label: "Leave & Availability", icon: CalendarOff },
  { to: "/actions", label: "Actions", icon: ListTodo },
  { to: "/auto-scheduler", label: "Auto-Scheduler", icon: Wand2, perm: "schedule_jobs", module: "auto_scheduler" },
  { to: "/invoicing", label: "Invoicing", icon: Wallet, perm: "view_financial", module: "invoicing" },
  { to: "/reports", label: "Reports", icon: BarChart3, perm: "generate_reports", module: "reporting" },
  { to: "/documents", label: "Documents", icon: FolderOpen, module: "documents" },
  { to: "/communications", label: "Communications", icon: Mail, perm: "view_clients", module: "communications" },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/users", label: "Users & Roles", icon: ShieldCheck, perm: "manage_users" },
  { to: "/audit", label: "Audit Log", icon: History, perm: "view_audit_logs" },
  { to: "/import", label: "Data Import", icon: FileUp, perm: "manage_system_settings", module: "data_import" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, perm: "manage_system_settings" },
];

const MOBILE_NAV = [
  { to: "/dashboard", label: "Jobs", icon: Briefcase },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/clients", label: "Clients", icon: Building2, perm: "view_clients" },
  { to: "/notifications", label: "Alerts", icon: Bell },
];

function NavItems({ items, onClick }) {
  return (
    <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5 seq-scroll-forest">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          onClick={onClick}
          data-testid={`nav-${it.to.slice(1)}`}
          className={({ isActive }) =>
            `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
              isActive
                ? "bg-seq-green/15 text-white font-medium border-l-2 border-seq-green pl-[10px]"
                : "text-emerald-100/70 hover:bg-white/5 hover:text-white"
            }`
          }
        >
          <it.icon size={18} className="shrink-0" />
          <span className="truncate">{it.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef();

  useEffect(() => {
    if (q.length < 2) { setRes(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setRes(data);
        setOpen(true);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); nav(path); };
  const groups = res ? [
    ["Clients", res.clients, (x) => `/clients/${x.id}`, (x) => x.client_name],
    ["Sites", res.sites, (x) => `/sites`, (x) => x.site_name],
    ["Contracts", res.contracts, () => `/contracts`, (x) => x.contract_name],
    ["Consultants", res.consultants, () => `/consultants`, (x) => x.name],
    ["Visits", res.visits, () => `/calendar`, (x) => `${x.visit_ref} — ${x.job_title}`],
  ].filter(([, arr]) => arr && arr.length) : [];

  return (
    <div className="relative flex-1 max-w-md" ref={ref}>
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => res && setOpen(true)}
        placeholder="Search clients, sites, visits, PO…"
        className="w-full pl-9 pr-3 py-2 text-sm bg-slate-100 border border-transparent rounded-lg focus:bg-white focus:border-seq-green focus:ring-2 focus:ring-seq-green/20 outline-none transition-colors"
      />
      {open && (
        <div className="absolute top-11 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-96 overflow-y-auto z-50">
          {groups.length === 0 && <div className="p-4 text-sm text-slate-400">No matches</div>}
          {groups.map(([label, arr, path, text]) => (
            <div key={label}>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-400 bg-slate-50">{label}</div>
              {arr.map((x) => (
                <button key={x.id} onClick={() => go(path(x))} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center justify-between">
                  <span className="truncate">{text(x)}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ImpersonationBanner({ name, asUser }) {
  const { exitAccount } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const exit = async () => {
    setBusy(true);
    try { await exitAccount(); nav("/platform/accounts"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-[#0B1627] text-white text-sm px-4 py-2 flex items-center gap-3 flex-wrap" data-testid="impersonation-banner">
      <span className="h-2 w-2 rounded-full bg-[#009b7f] animate-pulse" />
      <span>Platform admin view — you are inside <strong data-testid="impersonation-account-name">{name}</strong>
        {asUser && <> viewing as <strong data-testid="impersonation-user-name">{asUser}</strong></>}. Changes are logged under your name.</span>
      <button onClick={exit} disabled={busy} data-testid="exit-account-btn"
        className="ml-auto px-3 py-1 rounded-md bg-[#009b7f] hover:bg-[#00876e] text-white text-xs font-medium transition-colors">
        {busy ? "Exiting…" : "Exit to Amden Control"}
      </button>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [unread, setUnread] = useState(0);
  const loc = useLocation();
  const umRef = useRef();

  const enabledModules = Array.isArray(user?.tenant?.enabled_modules) ? user.tenant.enabled_modules : null; // null = SEQ/legacy or superadmin -> show all
  const moduleOk = (it) => !it.module || !enabledModules || enabledModules.includes(it.module);
  const items = NAV.filter((it) => (!it.perm || can(user, it.perm)) && moduleOk(it));
  const mobileItems = MOBILE_NAV.filter((it) => (!it.perm || can(user, it.perm)) && moduleOk(it));
  const accent = user?.tenant?.brand_color || "";

  useEffect(() => {
    document.documentElement.style.setProperty("--brand-accent", accent || "#009b7f");
  }, [accent]);

  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  useEffect(() => {
    const load = async () => {
      try { const { data } = await api.get("/notifications/unread-count"); setUnread(data.count); } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [loc.pathname]);

  useEffect(() => {
    const h = (e) => { if (umRef.current && !umRef.current.contains(e.target)) setUserMenu(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col fixed inset-y-0 left-0 w-60 bg-seq-forest z-40">
        <div className="h-16 flex items-center px-4 border-b border-white/10 shrink-0">
          <SeqLogo />
        </div>
        <NavItems items={items} />
        <div className="p-3 border-t border-white/10 text-[10px] text-slate-300/40 text-center">
          Amden Control · v1.0
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-slate-900/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-seq-forest flex flex-col animate-fade-in">
            <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
              <SeqLogo />
              <button onClick={() => setMobileOpen(false)} className="text-white/70" data-testid="close-drawer-btn"><X size={20} /></button>
            </div>
            <NavItems items={items} onClick={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="md:pl-60">
        {user?.impersonating && <ImpersonationBanner name={user.tenant?.name} asUser={user.impersonated_by ? `${user.name} (${user.role_name})` : null} />}
        <div className="h-1 w-full" style={{ backgroundColor: "var(--brand-accent)" }} data-testid="brand-accent-bar" />
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200 flex items-center gap-3 px-4">
          <button className="md:hidden text-slate-600" onClick={() => setMobileOpen(true)} data-testid="open-drawer-btn"><Menu size={22} /></button>
          <div className="md:hidden"><SeqMark /></div>
          {user?.tenant?.name && (
            <div className="hidden md:flex items-center gap-2 pr-3 mr-1 border-r border-slate-200" data-testid="account-brand">
              {user.tenant.logo_url
                ? <img src={user.tenant.logo_url.startsWith("http") ? user.tenant.logo_url : `${process.env.REACT_APP_BACKEND_URL}${user.tenant.logo_url}?auth=${localStorage.getItem("seq_token") || ""}`} alt={user.tenant.name} className="h-8 w-8 rounded-md object-contain bg-slate-50 border" style={{ borderColor: (accent || "#e2e8f0") }} />
                : <div className="h-8 w-8 rounded-md flex items-center justify-center text-xs font-bold" style={{ backgroundColor: (accent || "#009b7f") + "1A", color: accent || "#009b7f" }}>{initials(user.tenant.name)}</div>}
              <span className="text-sm font-semibold text-slate-800 max-w-[180px] truncate">{user.tenant.name}</span>
            </div>
          )}
          <div className="hidden sm:flex flex-1"><GlobalSearch /></div>
          <div className="flex-1 sm:hidden" />
          <NavLink to="/notifications" className="relative p-2 text-slate-500 hover:text-slate-800" data-testid="header-notifications-btn">
            <Bell size={20} />
            {unread > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-semibold">{unread}</span>}
          </NavLink>
          <div className="relative" ref={umRef}>
            <button onClick={() => setUserMenu((v) => !v)} data-testid="user-menu-btn" className="flex items-center gap-2 pl-2">
              <div className="h-9 w-9 rounded-full bg-seq-green text-white flex items-center justify-center text-sm font-semibold">{initials(user?.name)}</div>
              <div className="hidden sm:block text-left leading-tight">
                <div className="text-sm font-medium text-slate-800">{user?.name}</div>
                <div className="text-[11px] text-slate-400">{user?.role_name}</div>
              </div>
              <ChevronDown size={16} className="text-slate-400 hidden sm:block" />
            </button>
            {userMenu && (
              <div className="absolute right-0 top-12 w-52 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50">
                <div className="px-3 py-2 border-b border-slate-100">
                  <div className="text-sm font-medium text-slate-800">{user?.name}</div>
                  <div className="text-xs text-slate-400">{user?.email}</div>
                  {user?.is_demo && <span className="mt-1 inline-block text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">DEMO ACCOUNT</span>}
                </div>
                <button onClick={logout} data-testid="logout-btn" className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-2">
                  <LogOut size={16} /> Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 pb-24 md:pb-8 max-w-[1600px] mx-auto animate-fade-in">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 flex">
        {mobileItems.map((it) => (
          <NavLink key={it.to} to={it.to} data-testid={`bottomnav-${it.to.slice(1)}`}
            className={({ isActive }) => `flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[11px] ${isActive ? "text-seq-green" : "text-slate-400"}`}>
            <it.icon size={20} />
            <span>{it.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
